import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import styles from './AllocationDetailsPopup.module.scss';
import ProfilePicture from '../../../../components/profilePicture/ProfilePicture';
import Icon from '../../../../components/icon/Icon';
import Button from '../../../../components/button/Button';
import Modal from '../../../../components/modal/Modal';
import { useAuth } from '../../../../contexts/AuthContext';

const AllocationDetailsPopup = ({
  allocations = [],
  student,
  date,
  rawDate,
  position,
  onClose,
  onEdit,
  onDuplicate,
  onDelete,
  onCancel,
  isLoading = false,
  suppressClickOutside = false
}) => {
  const { role, can } = useAuth();
  const canCreateSchedule = role !== 'Program Coordinator' || can('canCreateSchedule');
  const canEditSchedule = role !== 'Program Coordinator' || can('canEditSchedule');
  const canDeleteSchedule = role !== 'Program Coordinator' || can('canDeleteSchedule');
  const popupRef = useRef(null);
  const isPastDate = rawDate ? new Date(rawDate) < new Date(new Date().toDateString()) : false;
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    if (!popupRef.current || !position) return;
    const rect = popupRef.current.getBoundingClientRect();
    const MARGIN = 10;
    if (rect.bottom > window.innerHeight - MARGIN) {
      const overflow = rect.bottom - (window.innerHeight - MARGIN);
      popupRef.current.style.top = `${popupRef.current.offsetTop - overflow}px`;
    }
  }, [position, allocations]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (deletingId || suppressClickOutside) return;
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target) &&
        !event.target.closest('.allocated-cell')
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose, deletingId, suppressClickOutside]);

  if (!position) return null;

  const studentName = student ? `${student.firstName} ${student.lastName}` : 'Unknown Student';

  return (
    <>
      {createPortal(
        <div
          className={styles.popup}
          style={{
            position: position.position || 'absolute',
            ...(position.top !== undefined && { top: `${position.top}px` }),
            ...(position.bottom !== undefined && { bottom: `${position.bottom}px` }),
            ...(position.left !== undefined && { left: `${position.left}px` }),
            ...(position.right !== undefined && { right: `${position.right}px` }),
            transform: 'none'
          }}
          ref={popupRef}
        >
          <button className={styles.closeBtn} onClick={onClose}>
            <Icon name="plus" className={styles.crossIcon} />
          </button>

          <div className={styles.header}>
            <div className={styles.userInfoRow}>
              <ProfilePicture
                profilePicture={student?.profilePicture}
                firstName={student?.firstName}
                lastName={student?.lastName}
                size="xsm"
              />
              <h3 className={styles.studentName}>{studentName}</h3>
            </div>
            <div className={styles.dateRow}>
              <p className={styles.dateText}>{date}</p>
            </div>
          </div>

          <div className={styles.divider} />

          <div className={styles.allocationsList}>
            {allocations.map((allocation, index) => (
              <div key={allocation.id}>
                <div className={styles.allocationCard}>
                  <div className={styles.allocationTitleRow}>
                    <div className={styles.titleLeft}>
                      <span className={styles.greenDot} />
                      <h4 className={styles.allocationTitle}>Allocation {index + 1}</h4>
                    </div>
                  </div>

                  <div className={styles.detailsGrid}>
                    <div className={styles.detailLabel}>Specialty</div>
                    <div className={styles.detailValue}>{allocation.speciality?.name || '-'}</div>
                    <div className={styles.detailLabel}>Clinical Site</div>
                    <div className={styles.detailValue}>{allocation.clinicalSite?.name || '-'}</div>
                    <div className={styles.detailLabel}>Instructor</div>
                    <div className={styles.detailValue}>
                      {allocation.instructor ? (
                        <div className={styles.instructorRow}>
                          <ProfilePicture
                            profilePicture={allocation.instructor.profilePicture}
                            firstName={allocation.instructor.firstName}
                            lastName={allocation.instructor.lastName}
                            size="xs"
                          />
                          <span>{`${allocation.instructor.firstName} ${allocation.instructor.lastName}`}</span>
                        </div>
                      ) : '-'}
                    </div>
                  </div>
                  <div className={styles.cardActions}>
                    <div className={styles.leftActions}>
                      <button className={styles.iconBtn} onClick={() => onDuplicate(allocation)} disabled={isPastDate || !canCreateSchedule} title="Duplicate">
                        <Icon name="copy" />
                      </button>
                      <button className={`${styles.iconBtn} ${styles.deleteIconBtn}`} onClick={() => setDeletingId(allocation.id)} disabled={isPastDate || !canDeleteSchedule} title="Delete">
                        <Icon name="trash" />
                      </button>
                      {allocation.status !== 'Cancelled' && (
                        <button className={styles.iconBtn} onClick={() => onEdit(allocation)} disabled={isPastDate || !canEditSchedule} title="Edit">
                          <Icon name="edit" />
                        </button>
                      )}
                    </div>
                    {allocation.status === 'Cancelled' ? (
                      <span className={styles.cancelledLabel}>Cancelled</span>
                    ) : allocation.instructor && (
                      <button
                        className={styles.cancelBtn}
                        onClick={() => onCancel(allocation.id)}
                        disabled={isLoading || isPastDate}
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
                {index < allocations.length - 1 && <div className={styles.divider} />}
              </div>
            ))}

          </div>
        </div>,
        document.body
      )}

      {deletingId && (
        <Modal>
          <div className={styles.deleteConfirm}>
            <h2 className={styles.modalTitle}>Are you sure you want to delete this allocation?</h2>
            <div className={styles.buttons}>
              <Button
                label="Delete"
                size="small"
                variant="secondary"
                onClick={() => { onDelete(deletingId); setDeletingId(null); }}
              />
              <Button
                label="Cancel"
                size="small"
                variant="cancel"
                onClick={() => setDeletingId(null)}
              />
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

AllocationDetailsPopup.propTypes = {
  allocations: PropTypes.array,
  student: PropTypes.shape({
    id: PropTypes.number,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    profilePicture: PropTypes.string
  }),
  date: PropTypes.string,
  rawDate: PropTypes.string,
  position: PropTypes.shape({
    top: PropTypes.number,
    bottom: PropTypes.number,
    left: PropTypes.number,
    right: PropTypes.number,
    position: PropTypes.string,
    isNearBottom: PropTypes.bool
  }),
  onClose: PropTypes.func,
  onEdit: PropTypes.func,
  onDuplicate: PropTypes.func,
  onDelete: PropTypes.func,
  onCancel: PropTypes.func,
  isLoading: PropTypes.bool,
  suppressClickOutside: PropTypes.bool
};

export default AllocationDetailsPopup;
